import { FC } from 'react';
import PortfolioPageLayout from 'presentation/layout/modules/portfolio/PortfolioPageLayout';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import { t } from 'i18next';
import AppCard from 'presentation/components/shared/card/AppCard';
import { Form, Formik } from 'formik';
import {
  Box,
  Button,
  Center,
  FormLabel,
  Spinner,
  useToast,
  Text,
} from '@chakra-ui/react';
import FormSelectInput from 'presentation/components/shared/form/FormSelectInput';
import FormNumberInput from 'presentation/components/shared/form/FormNumberInput';
import usePeriodQueryParam from 'hooks/modules/period/usePeriodQueryParam';
import { useFetchHomeQuery } from 'rtkQuery/sharespro/queries/home';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import { CreateWithdrawalOrderRequestData } from 'rtkQuery/sharespro/types/withdrawal/createWithdrawalOrderRequestData.t';
import { useCreateWithdrawalOrderMutation } from 'rtkQuery/sharespro/queries/portfolio';
import { FormikErrors } from 'formik/dist/types';
import AppRoute from 'router/AppRoute';
import useFormError from 'hooks/shared/useFormError';
import { Color } from 'presentation/theme/colorScheme';

interface FormValues {
  amount: string;
  currency: string;
  walletAddress: string;
}

const CreateWithdrawalOrderPage: FC = (_props) => {
  const params = useParams<{ accountId: string }>();
  const accountId = Number(params.accountId);
  const [period] = usePeriodQueryParam();
  const { data, error, refetch, isFetching } = useFetchHomeQuery({
    period,
    account_id: accountId,
  });
  const selectedAccount = data?.accounts.items.find(
    (item) => item.id === accountId
  );
  const formErrorHandler = useFormError<FormValues>();
  const toast = useToast();
  const navigate = useNavigate();

  const [createWithdrawalOrder, { isLoading }] =
    useCreateWithdrawalOrderMutation();

  let content;
  if (data && selectedAccount) {
    const currencies = data.currencyBalances.map((balance) => balance.currency);
    const usdCash = Math.floor(selectedAccount.usdCash * 100) / 100;
    const wallets = selectedAccount.service.allowedWallets;

    content = (
      <Formik<FormValues>
        initialValues={{
          amount: '',
          currency: '',
          walletAddress: '',
        }}
        validate={(values) => {
          const errors: FormikErrors<FormValues> = {};

          // Проверка на пустое значение суммы
          if (!values.amount) {
            errors.amount = t('amountIsRequired')!;
          }
          if (Number(values.amount) < 1) {
            errors.amount = t('amountIsLow')!;
          }
          // Проверка на превышение доступной суммы
          if (Number(values.amount) > usdCash) {
            errors.amount = t('amountExceedsAvailable')!;
          }

          // Проверка на пустое значение валюты
          if (!values.currency) {
            errors.currency = t('currencyIsRequired')!;
          }

          return errors;
        }}
        onSubmit={async (values, formik) => {
          const request: CreateWithdrawalOrderRequestData = {
            amount: Number(values.amount),
            currency: Number(values.currency),
            account_id: accountId,
            wallet_address: values.walletAddress ? values.walletAddress : null,
          };
          createWithdrawalOrder(request)
            .unwrap()
            .then(() => {
              toast({
                title: t('withdrawalRequestCreatedSuccessfully'),
                status: 'success',
                duration: 10 * 1000,
                position: 'top-right',
                isClosable: true,
              });
              navigate(AppRoute.withdrawalOrders(accountId));
            })
            .catch((err) => formErrorHandler(err, formik));
        }}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <FormLabel mb="40px">{`${t('withdrawalFromAccount')} №${
              selectedAccount?.id
            }/${selectedAccount?.name}`}</FormLabel>

            <Box mb="20px">
              <FormNumberInput
                name="amount"
                onChange={(val) => {
                  setFieldValue('amount', val);
                }}
                value={values.amount}
                label={t('withdrowalAmount')}
                precision={2}
                step={0.01}
                postfix={
                  <Button
                    size="sm"
                    colorScheme={Color.Primary500}
                    textColor={Color.Primary500}
                    variant="ghost"
                    onClick={() => setFieldValue('amount', usdCash.toString())}
                  >
                    max
                  </Button>
                }
              />
              <Text fontSize="sm" color="gray.500" mt={2}>
                {t('available')}: {usdCash.toFixed(2)} USD
              </Text>
            </Box>

            <Box mb="40px">
              <FormSelectInput
                onChange={(val) => {
                  setFieldValue('currency', val.target.value);
                }}
                name="currency"
                label={t('currency')}
                value={values.currency ?? undefined}
              >
                <option value=""></option>
                {currencies.map((currency) => {
                  return (
                    <option key={currency.id} value={currency.id}>
                      {currency.code}
                    </option>
                  );
                })}
              </FormSelectInput>
            </Box>

            <Box mb="40px">
              <FormSelectInput
                onChange={(val) => {
                  setFieldValue('walletAddress', val.target.value);
                }}
                name="walletAddress"
                label={t('wallet')}
                value={values.walletAddress ?? undefined}
                isDisabled={wallets.length === 0}
              >
                <option value=""></option>
                {wallets.map((wallet) => {
                  return (
                    <option key={wallet} value={wallet}>
                      {wallet}
                    </option>
                  );
                })}
              </FormSelectInput>
            </Box>

            <Button
              type="submit"
              sx={{
                width: '100%',
              }}
              isLoading={isLoading}
            >
              {t('createRequest')}
            </Button>
          </Form>
        )}
      </Formik>
    );
  } else if (isFetching) {
    content = (
      <Center p="20px">
        <Spinner />
      </Center>
    );
  } else if (error) {
    content = (
      <ErrorContainer
        error={error}
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <>
      <PortfolioPageLayout>
        <GridHeading>{t('requestCreating')}</GridHeading>
        <AppCard p="40px">{content}</AppCard>
      </PortfolioPageLayout>
    </>
  );
};

export default CreateWithdrawalOrderPage;
